import { useEffect, useState, useRef, useCallback } from "react";
import { ExtractionSchema } from "../models/extraction";
import { z } from "zod";
import { fetchFastApi } from "../api/fastapi";
import { toast } from "sonner";
interface UseExtractionsProps {
  fetchOnLoad?: boolean;
}

export const useExtractions = ({
  fetchOnLoad = false,
}: UseExtractionsProps) => {
  const [extractions, setExtractions] = useState<
    z.infer<typeof ExtractionSchema>[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [offset, setOffset] = useState(0);
  const [hasMoreLogs, setHasMoreLogs] = useState(true);

  const hasFetched = useRef(false);

  const fetchExtractions = async () => {
    if (offset === 0) {
      setLoading(true);
    }
    try {
      const response = await fetchFastApi({
        method: "GET",
        path: `/extractions/`,
        query: {
          offset: offset.toString(),
        },
      });
      const data = ExtractionSchema.array().parse(response.extractions);
      setExtractions((prev) => [...prev, ...data]);
      setOffset(offset + 10);
      if (data.length < 10) {
        setHasMoreLogs(false);
      }
    } catch (error) {
      console.error(error);
      toast.error("Error fetching extractions");
    }
    setLoading(false);
  };

  const fetchExtraction = useCallback(
    async (extractionId: string) => {
      try {
        const response = await fetchFastApi({
          method: "GET",
          path: `/extractions/extraction/${extractionId}/status`,
        });
        const data = ExtractionSchema.parse(response.extraction);

        setExtractions((prev) => {
          const newExtractions = prev.map((extraction) =>
            extraction.extractionId === extractionId ? data : extraction,
          );
          return newExtractions;
        });
      } catch (error) {
        console.error(error);
        toast.error("Error fetching extraction");
      }
    },
    [setExtractions],
  );

  useEffect(() => {
    if (fetchOnLoad && !hasFetched.current) {
      fetchExtractions();
      hasFetched.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchOnLoad]);

  return {
    extractions,
    loading,
    fetchExtraction,
    fetchExtractions,
    hasMoreLogs,
  };
};
